/* eslint-disable @typescript-eslint/naming-convention */
// ---------------------------------------------------------------------------
// localStorage keys
// ---------------------------------------------------------------------------
export const WIDGET_STATE_KEY = 'ai_widget_state';
const WIDGET_STATE_VERSION = 1;
// ---------------------------------------------------------------------------
// Widget state helpers
// ---------------------------------------------------------------------------
/** Build the localStorage key for global or scope-isolated widget state. */
function widgetStateKey(scope) {
    return scope ? `${WIDGET_STATE_KEY}:${scope}` : WIDGET_STATE_KEY;
}
/** Load the persisted widget state for the requested conversation scope. */
export function loadWidgetState(scope) {
    try {
        const stored = localStorage.getItem(widgetStateKey(scope));
        if (!stored)
            return null;
        const parsed = JSON.parse(stored);
        // Version check for future migrations
        if (parsed.version !== WIDGET_STATE_VERSION) {
            console.warn('[widgetStateStorage] Version mismatch, clearing state');
            clearWidgetState(scope);
            return null;
        }
        return parsed;
    }
    catch (error) {
        console.warn('[widgetStateStorage] Failed to load widget state:', error);
        return null;
    }
}
/** Merge and persist widget state for the requested conversation scope. */
export function saveWidgetState(state, scope) {
    var _a, _b;
    try {
        const current = loadWidgetState(scope);
        const hasActiveSessionId = Object.prototype.hasOwnProperty.call(state, 'activeSessionId');
        const merged = {
            version: WIDGET_STATE_VERSION,
            activeSessionId: hasActiveSessionId ? (_a = state.activeSessionId) !== null && _a !== void 0 ? _a : null : (_b = current === null || current === void 0 ? void 0 : current.activeSessionId) !== null && _b !== void 0 ? _b : null,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(widgetStateKey(scope), JSON.stringify(merged));
    }
    catch (error) {
        console.warn('[widgetStateStorage] Failed to save widget state:', error);
    }
}
/** Remove persisted widget state for the requested conversation scope. */
export function clearWidgetState(scope) {
    try {
        localStorage.removeItem(widgetStateKey(scope));
    }
    catch (error) {
        console.warn('[widgetStateStorage] Failed to clear widget state:', error);
    }
}
// ---------------------------------------------------------------------------
// Stale-conversation policy — Claude-style "fresh start" after inactivity
// ---------------------------------------------------------------------------
/**
 * How long a conversation can sit idle before we stop auto-resuming it on mount.
 * Past this, reopening the widget (e.g. a tab opened the next day) drops the user
 * on the new-conversation screen instead of silently rehydrating a days-old
 * thread. The old conversation is NOT deleted — it stays reachable from history.
 */
export const STALE_CONVERSATION_MS = 8 * 60 * 60 * 1000; // 8 hours
/**
 * Whether a conversation is too idle to auto-resume.
 *
 * Uses the server-authoritative `lastActivityAt` (bumped per message by Rails'
 * Ai::ChatSessionSync), falling back to `createdAt` for sessions with no recorded
 * activity yet. Note we deliberately do NOT use the localStorage `timestamp`:
 * that is rewritten to "now" on every mount (the restore effect re-saves), so it
 * cannot detect staleness across reloads.
 *
 * Fail-safe: a missing or unparseable timestamp is treated as NOT stale, so a bad
 * value never costs the user their active thread.
 */
export function isConversationStale(lastActivityAt, createdAt, now = Date.now()) {
    const raw = lastActivityAt !== null && lastActivityAt !== void 0 ? lastActivityAt : createdAt;
    if (!raw)
        return false;
    const lastActive = new Date(raw).getTime();
    if (Number.isNaN(lastActive))
        return false;
    return now - lastActive > STALE_CONVERSATION_MS;
}
